import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Typography, Divider, Tag, Space, Table } from 'antd';
import { SwapOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import httpInjectorService from 'services/http-injector.service';

const { Title, Text } = Typography;

export default function CompareHistory() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { spaceid, pageid } = useParams();

  const { oldVers, currentVers, oldData, currentData } = state || {};

  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(false);

  const getCompareData = async () => {
    try {
      setLoading(true);

      const payload = {
        space_id: Number(spaceid),
        item_id: Number(pageid),
        oldVers,
        currentVers,
      };

      const response = await httpInjectorService.compareVersion(payload);

      if (response.status === 'success') {
        setCompareData(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const renderNode = (node) => {
    if (!node) return null;

    if (typeof node === 'string') {
      return <span>{node}</span>;
    }
    if (node.text) {
      return <span>{node.text}</span>;
    }

    if (node.type === 'date') {
      return <span>📅 {node.date}</span>;
    }

    if (node.type === 'paragraph') {
      if (!node.children || node.children.length === 0) {
        return <div style={{ opacity: 0.6 }}>[Empty Paragraph]</div>;
      }

      return <div>{node.children.map(renderNode)}</div>;
    }

    if (node.type === 'table') {
      return (
        <table border="1" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {node.children?.map((row, i) => (
              <tr key={i}>
                {row.children?.map((cell, j) => (
                  <td key={j} style={{ padding: 6 }}>
                    {cell.children?.map(renderNode)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (node.type === 'image') {
      return (
        <img src={node.src} alt="" style={{ maxWidth: 200, borderRadius: 6 }} />
      );
    }

    if (node.type === 'file') {
      return (
        <div
          style={{
            padding: 8,
            border: '1px solid #ddd',
            borderRadius: 6,
            display: 'inline-block',
          }}
        >
          📎 {node.name || 'File'}
        </div>
      );
    }

    if (node.type === 'checkbox') {
      return (
        <div>
          <input type="checkbox" checked={node.checked} readOnly />{' '}
          {node.children?.map(renderNode)}
        </div>
      );
    }

    if (node.type === 'divider') {
      return <hr />;
    }

    if (node.type === 'panel') {
      return (
        <div
          style={{
            background: '#f5f5f5',
            padding: 10,
            borderRadius: 6,
            borderLeft: '4px solid #1890ff',
          }}
        >
          {node.children?.map(renderNode)}
        </div>
      );
    }

    if (node.type === 'layout') {
      return (
        <div style={{ display: 'flex', gap: 10 }}>
          {node.children?.map((col, i) => (
            <div key={i} style={{ flex: 1 }}>
              {col.children?.map(renderNode)}
            </div>
          ))}
        </div>
      );
    }

    if (node.children) {
      return <div>{node.children.map(renderNode)}</div>;
    }

    return <span>[Unknown]</span>;
  };

  const formatDiff = (data) => {
    return data.map((item, index) => ({
      id: index,
      type: item.type,
      value: item.value,
      before: item.before,
      after: item.after,
    }));
  };

  useEffect(() => {
    if (oldVers && currentVers) {
      getCompareData();
    }
  }, [oldVers, currentVers]);

  const renderDiff = (diffs) => {
    return diffs.map((item) => {
      if (item.type === 'content_added') {
        return (
          <div
            style={{
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              padding: 10,
              borderRadius: 6,
              marginBottom: 12,
            }}
          >
            <div style={{ color: 'green', marginBottom: 4 }}>+ Added</div>
            {renderNode(item.value)}
          </div>
        );
      }

      if (item.type === 'content_removed') {
        return (
          <div
            style={{
              background: '#fff1f0',
              border: '1px solid #ffa39e',
              padding: 10,
              borderRadius: 6,
              marginBottom: 12,
            }}
          >
            <div style={{ color: 'red', marginBottom: 4 }}>- Removed</div>
            {renderNode(item.value)}
          </div>
        );
      }

      if (item.type === 'content_modified') {
        return (
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                background: '#fff1f0',
                border: '1px solid #ffa39e',
                padding: 10,
                borderRadius: 6,
                marginBottom: 6,
              }}
            >
              <div style={{ color: 'red' }}>- Before</div>
              {renderNode(item.before)}
            </div>

            <div
              style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                padding: 10,
                borderRadius: 6,
              }}
            >
              <div style={{ color: 'green' }}>+ After</div>
              {renderNode(item.after)}
            </div>
          </div>
        );
      }

      return null;
    });
  };

  if (!state) {
    return (
      <Card>
        <Text>No comparison data found</Text>
      </Card>
    );
  }

  return (
    <Card style={{ borderRadius: 12 }} loading={loading}>
      <Title level={4} style={{ marginBottom: 20 }}>
        Content Comparison
      </Title>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={16}>
          <Card type="inner" title="Version Compared" bordered={false}>
            <Row align="middle" justify="space-between">
              <Col span={10}>
                <Space direction="vertical">
                  <Tag color="blue">V{currentData?.version_number}</Tag>
                  <Text type="secondary">
                    {new Date(currentData?.created_at).toLocaleDateString()}
                  </Text>
                  <Text>{currentData?.author}</Text>
                </Space>
              </Col>

              <Col span={4} style={{ textAlign: 'center' }}>
                <SwapOutlined style={{ fontSize: 20 }} />
              </Col>

              <Col span={10}>
                <Space direction="vertical">
                  <Tag color="purple">V{oldData?.version_number}</Tag>
                  <Text type="secondary">
                    {new Date(oldData?.created_at).toLocaleDateString()}
                  </Text>
                  <Text>{oldData?.author}</Text>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            size="small"
            title={
              <Space>
                <InfoCircleOutlined />
                Note
              </Space>
            }
          >
            <Space direction="vertical">
              <Text style={{ color: 'green' }}>● Line added</Text>
              <Text style={{ color: 'red' }}>● Line removed</Text>
              <Text style={{ color: '#1890ff' }}>● Formatting changed</Text>
            </Space>
          </Card>
        </Col>
      </Row>

      <Divider />

      <Text
        type="link"
        onClick={() => navigate(-1)}
        style={{ cursor: 'pointer' }}
      >
        View version history
      </Text>

      {compareData && (
        <>
          <Divider />
          <Title level={5}>Changes Preview</Title>

          <div
            style={{
              background: '#fafafa',
              padding: 12,
              borderRadius: 8,
              maxHeight: 400,
              overflow: 'auto',
            }}
          >
            {renderDiff(formatDiff(compareData))}
          </div>
        </>
      )}
    </Card>
  );
}
